'use client'

import { useState, useEffect, useMemo, useCallback } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useRollbar } from '@rollbar/react'
import { isPresent } from '@farmai-shared/LFUtils'

type Update = { key: string; value: string | null | undefined }

let internalNavigations = 0

export const getInternalNavigations = () => internalNavigations

export const useLFSearchParams = () => {
  const rollbar = useRollbar()
  const isBrowser = typeof window !== 'undefined'

  if (!isBrowser) {
    return {
      searchParams: new URLSearchParams(),
      setParams: () => {
        rollbar.error('setParams called in a non-browser environment')
      },
      setParam: () => {
        rollbar.error('setParam called in a non-browser environment')
      },
    }
  }

  const pathname = usePathname()
  const nextParams = useSearchParams()
  const [qs, setQs] = useState(() => nextParams.toString())
  const searchParams = useMemo(() => new URLSearchParams(qs), [qs])

  useEffect(() => {
    setQs(nextParams.toString())
  }, [nextParams])

  useEffect(() => {
    const onPop = () => setQs(window.location.search.slice(1))
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const buildQueryString = useCallback(
    (updates: { key: string; value: string | null | undefined }[]) => {
      const base = new URLSearchParams(searchParams)
      const buckets = new Map<string, Set<string>>()

      for (const { key, value } of updates) {
        if (key.endsWith('[]') && isPresent(value)) {
          if (!buckets.has(key)) buckets.set(key, new Set())
          buckets.get(key)!.add(value as string)
        } else if (!isPresent(value)) {
          base.delete(key)
        } else {
          base.set(key, value as string)
        }
      }

      for (const [key, vals] of buckets) {
        base.delete(key)
        vals.forEach((v) => base.append(key, v))
      }

      return base.toString()
    },
    [searchParams]
  )

  const navigateQS = useCallback(
    (nextQS: string) => {
      const { pathname: curPath, search: curSearch } = window.location
      const href = `${pathname}${nextQS ? `?${nextQS}` : ''}`
      const currentHref = curPath + curSearch

      if (href === currentHref) return

      internalNavigations += 1
      window.history.replaceState({}, '', href)
      setQs(nextQS)
    },
    [pathname]
  )

  const setParams = useCallback(
    (updates: Update[]) => {
      try {
        const nextQS = buildQueryString(updates)
        navigateQS(nextQS)
      } catch (err) {
        rollbar.error('useLFSearchParams#setParams', err)
      }
    },
    [buildQueryString, navigateQS, rollbar]
  )

  const setParam = useCallback(
    (key: string, value: string | null | undefined) => {
      setParams([{ key, value }])
    },
    [setParams]
  )

  return { searchParams, setParams, setParam }
}
